import { useState } from 'react';
import { ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { appsAdvApiRouterRateRide } from '@/lib/api';

interface RideRatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  rideId: string;
  rideTitle: string;
  onRatingComplete: () => void;
}

export const RideRatingModal = ({ 
  isOpen, 
  onClose, 
  rideId, 
  rideTitle, 
  onRatingComplete 
}: RideRatingModalProps) => {
  const [rating, setRating] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleRating = async (thumbsUp: boolean) => {
    if (submitting) return;

    try {
      setSubmitting(true);
      await appsAdvApiRouterRateRide(rideId, { thumbs_up: thumbsUp });

      toast({
        title: "Rating Submitted",
        description: `Thanks for rating "${rideTitle}"!`,
        variant: "default"
      });
      
      onRatingComplete();
      onClose();
    } catch (error: any) {
      toast({
        title: "Rating Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setRating(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-adv-charcoal border-adv-darkGray">
        <DialogHeader>
          <DialogTitle className="text-white text-xl">
            Rate Your Ride Experience
          </DialogTitle>
          <DialogDescription className="text-adv-lightGray">
            How was your experience with "{rideTitle}"? Your feedback helps other riders!
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-6">
          <div className="flex justify-center space-x-8">
            <Button
              variant="outline"
              size="lg"
              className={`h-20 w-20 rounded-full border-2 transition-all duration-200 ${
                rating === true 
                  ? 'border-green-500 bg-green-500/20 text-green-500' 
                  : 'border-adv-mediumGray hover:border-green-500 hover:text-green-500'
              }`}
              onClick={() => setRating(true)}
              disabled={submitting}
            >
              <ThumbsUp size={32} />
            </Button>
            
            <Button
              variant="outline"
              size="lg"
              className={`h-20 w-20 rounded-full border-2 transition-all duration-200 ${
                rating === false 
                  ? 'border-red-500 bg-red-500/20 text-red-500' 
                  : 'border-adv-mediumGray hover:border-red-500 hover:text-red-500'
              }`}
              onClick={() => setRating(false)}
              disabled={submitting}
            >
              <ThumbsDown size={32} />
            </Button>
          </div>
          
          {rating !== null && (
            <div className="mt-6 text-center">
              <p className="text-adv-lightGray mb-4">
                {rating ? '👍 Great ride!' : '👎 Thanks for the feedback'}
              </p>
              <p className="text-sm text-adv-mediumGray">
                Click "Submit Rating" to confirm your choice
              </p>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={submitting}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={() => rating !== null && handleRating(rating)}
            disabled={rating === null || submitting}
            className="btn-primary w-full sm:w-auto"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              'Submit Rating'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
